import { useHavePermission } from '@/hooks/commons/useHavePermission'
import { useQuery } from '@/hooks/commons/useQuery'
import {
  createRouteForLink,
  getBranchFromPathUrl,
} from '@/utils/helpers/urlHelper'
import { servicePermissions } from '@/constants/permissions'
import { usePermissions } from '@/hooks/commons/usePermissions'
import { Menu, MenuProps, Button } from 'antd'
import { InboxOutlined, OrderedListOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useAccess } from '@/hooks/commons/useAccess'

import Box from '@/assets/images/icons/box.svg?react'
import Boxes from '@/assets/images/icons/boxes.svg?react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { ModalCategory } from './modalCategory'
import { HeadingService } from './heading'
import { pageUrl } from '@/constants/url'
import { BundleFormDrawer } from './bundleFormDrawer'
import { useGetCategories } from '@/hooks/category/useGetCategories'
import { CategoryOrderDrawer } from './categoryOrderDrawer'
import { useTranslation } from 'react-i18next'

type MenuItem = Required<MenuProps>['items'][number]

export default function ServiceLayout() {
  const { t } = useTranslation()
  const { t: tCatalog } = useTranslation('catalog')
  const { havePermissionAccess } = usePermissions()
  const { serviceMaterialEnabled } = useAccess()
  const branch = getBranchFromPathUrl()
  const { queryParsed, removeQuery, updateQuery } = useQuery()
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false)
  const { data: categories } = useGetCategories({ branch })
  const handleCancel = () => {
    removeQuery(['addCategory', 'addBundle', 'editBundle'])
  }
  useHavePermission({ permission: servicePermissions.read })
  const handleSaveCategory = () => {
    updateQuery({ refreshCategory: new Date().getTime(), addCategory: '' })
  }
  const location = useLocation()
  const activePage = location.pathname.split('/').at(-1) as string
  const items: MenuItem[] = [
    {
      label: (
        <Link to={createRouteForLink(pageUrl.services)}>
          {t('menu.services')}
        </Link>
      ),
      key: 'services',
      icon: <Box className="h-[14px] w-[14px]" />,
    },
    {
      label: (
        <Link to={createRouteForLink(pageUrl.bundles)}>
          {t('common.bundles')}
        </Link>
      ),
      key: 'bundles',
      icon: <Boxes className="h-[16px] w-[16px]" />,
    },
    ...(serviceMaterialEnabled
      ? [
          {
            label: (
              <Link to={createRouteForLink(pageUrl.materials)}>
                {tCatalog('catalog.inventory')}
              </Link>
            ),
            key: 'materials',
            icon: <InboxOutlined />,
          },
        ]
      : []),
  ]
  const openBundleForm =
    queryParsed.addBundle === 'true' || !!queryParsed.editBundle
  return (
    <>
      <BundleFormDrawer
        bundleId={queryParsed.editBundle}
        onClose={handleCancel}
        open={
          openBundleForm &&
          havePermissionAccess({ permission: servicePermissions.create })
        }
      />
      <ModalCategory
        onCancel={handleCancel}
        open={
          queryParsed.addCategory === 'true' &&
          havePermissionAccess({ permission: servicePermissions.create })
        }
        onSave={handleSaveCategory}
        branch={branch}
      />
      {activePage !== 'materials' && <HeadingService />}
      <div className="flex items-center justify-between mt-4 bg-white">
        <Menu
          selectedKeys={[activePage]}
          mode="horizontal"
          items={items}
          className="flex-1"
        />
        {activePage === 'services' && (
          <Button
            type="text"
            icon={<OrderedListOutlined />}
            onClick={() => setCategoryDrawerOpen(true)}
            className="text-gray-500 hover:text-gray-700"
          />
        )}
      </div>

      <CategoryOrderDrawer
        open={categoryDrawerOpen}
        onCancel={() => setCategoryDrawerOpen(false)}
        categories={categories || []}
      />

      <Outlet />
    </>
  )
}
